import { initPrivateAttachmentLinks } from '../../components/private-attachment-links.js';
import { canAnimateElement, readCssTimeMs } from '../../components/motion-policy.js';
import { cancelAnimations, playAnimation, settleAnimations } from '../../components/motion-runner.js';
import {
    buildNotesSearchPageUrl,
    NOTES_PAGE_REQUEST_ERROR,
    normalizeNotesQuery,
    requestNotesPage,
} from './page-client.js';
import { initGameDetailsNoteAttachmentPreview } from './attachment-preview.js';

const AUTO_RETRY_DELAY_MS = 800;
const NOTES_PREFETCH_MARGIN = '0px 0px 600px 0px';
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_RESULTS_SWAP_DELAY_MS = 200;
/**
 * A note link from a ranking arrives with the opening of that note as the query, so almost every word of
 * the note is a match and the highlight covers the whole thing. Arriving that way the note itself is the
 * destination, not the words - the class mutes the marks until the search field is used as a search.
 * Only that link carries ARRIVED_FROM_LINK_PARAM; any other page starting with a query (a reload, a
 * bookmark, the redirect after a note edit or delete) is a search and keeps its highlight.
 */
const ARRIVED_FROM_LINK_CLASS = 'game-notes-highlight-muted';
const ARRIVED_FROM_LINK_PARAM = 'notes_from';
const ARRIVED_FROM_LINK_VALUE = 'ranking';
/**
 * Any of these before the notes arrive means the user has taken over the scroll. Not `scroll` - the native
 * anchor jump fires it too.
 */
const LANDING_INTENT_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'];

export function initGameDetailsNotesStreamController(documentRef = document, fetchImpl = fetch) {
    const notesSection = documentRef.querySelector('[data-game-notes-section]');
    if (!(notesSection instanceof HTMLElement)) {
        return null;
    }

    const sessionAlert = notesSection.querySelector('[data-game-note-session-alert]');
    const sessionRefreshButton = notesSection.querySelector('[data-game-note-session-refresh]');
    const attachmentPreview = initGameDetailsNoteAttachmentPreview(documentRef);

    if (sessionRefreshButton instanceof HTMLButtonElement) {
        sessionRefreshButton.addEventListener('click', () => {
            window.location.reload();
        });
    }

    const showSessionAlert = () => {
        attachmentPreview?.close();

        if (!(sessionAlert instanceof HTMLElement) || sessionAlert.hidden === false) {
            return;
        }

        sessionAlert.hidden = false;
    };

    const renderUnavailablePlaceholder = ({ link }) => {
        if (!(link instanceof HTMLAnchorElement)) {
            return;
        }

        link.classList.add('game-note-attachment-link-unavailable');
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        link.removeAttribute('target');
        link.removeAttribute('rel');

        const placeholder = document.createElement('div');
        placeholder.className = 'game-note-attachment-placeholder';
        placeholder.innerHTML = '<span class="game-note-attachment-placeholder-icon" aria-hidden="true">🖼</span><span class="game-note-attachment-placeholder-text">Unavailable</span>';

        link.replaceChildren(placeholder);
    };

    const count = notesSection.querySelector('[data-game-notes-count]');
    const searchForm = notesSection.querySelector('[data-game-notes-search-form]');
    const searchInput = notesSection.querySelector('[data-game-notes-search-input]');
    const searchClear = notesSection.querySelector('[data-game-notes-search-clear]');
    const browseRoot = notesSection.querySelector('[data-game-notes-stream="browse"]');
    const searchRoot = notesSection.querySelector('[data-game-notes-stream="search"]');
    const initialUrl = String(notesSection.dataset.notesInitialUrl ?? '').trim();

    if (
        !(count instanceof HTMLElement)
        || !(searchForm instanceof HTMLFormElement)
        || !(searchInput instanceof HTMLInputElement)
        || !(searchClear instanceof HTMLButtonElement)
        || !(browseRoot instanceof HTMLElement)
        || !(searchRoot instanceof HTMLElement)
        || initialUrl === ''
    ) {
        return null;
    }

    const createStream = (kind, root) => {
        const results = root.querySelector('[data-game-notes-results]');
        const pagination = root.querySelector('[data-game-notes-pagination]');
        const paginationStatus = root.querySelector('[data-game-notes-pagination-status]');
        const paginationAction = root.querySelector('[data-game-notes-pagination-action]');
        if (
            !(results instanceof HTMLElement)
            || !(pagination instanceof HTMLElement)
            || !(paginationStatus instanceof HTMLElement)
            || !(paginationAction instanceof HTMLButtonElement)
        ) {
            return null;
        }

        return {
            kind,
            root,
            results,
            pagination,
            paginationStatus,
            paginationAction,
            query: '',
            currentChunk: null,
            nextPageUrl: kind === 'browse' ? initialUrl : '',
            requestController: null,
            resumePending: false,
            autoRetryAvailable: false,
            paginationPhase: 'ready',
            paginationMotionRun: null,
            restartPending: false,
            restartHadContent: false,
            hasAttachmentWarning: false,
            hasLoadedContinuation: false,
            resultCount: 0,
        };
    };

    const browseStream = createStream('browse', browseRoot);
    const searchStream = createStream('search', searchRoot);
    if (browseStream === null || searchStream === null) {
        return null;
    }

    let hydratePrivateAttachments = null;
    try {
        hydratePrivateAttachments = initPrivateAttachmentLinks({
            root: notesSection,
            imageSelector: '[data-game-note-attachment-image]',
            linkSelector: '[data-game-note-attachment-link]',
            refreshUrlDataAttribute: 'refreshUrl',
            onActivate: attachmentPreview?.open,
            onSessionBlocked: showSessionAlert,
            renderUnavailable: renderUnavailablePlaceholder,
        });
    } catch (_error) {
        // Notes remain readable when optional signed-attachment hydration fails.
    }

    const baseUrl = String(globalThis.location?.href ?? 'http://localhost/');
    const initialPageUrl = new URL(initialUrl, baseUrl);
    let activeStream = null;
    let observer = null;
    let autoPaginationUnavailable = typeof globalThis.IntersectionObserver !== 'function';
    let countInitialized = false;
    let searchDebounceId = null;
    let isComposing = false;
    let browseScrollOffset = null;
    let pendingStreamHeight = null;
    let streamHeightRun = null;
    let streamRevealAnimation = null;
    let streamSwapId = 0;
    // A `#notes` redirect arrives before the notes exist, so the native jump stops short of the section. The
    // first initial load of the stream active at init finishes that landing once.
    let landingStream = null;

    const setSearchAvailability = (state) => {
        const enabled = state === 'ready';
        searchInput.disabled = !enabled;
        searchClear.disabled = !enabled;
        searchForm.classList.toggle('game-notes-search-unavailable', !enabled);
        searchInput.placeholder = state === 'error'
            ? 'Search unavailable'
            : (enabled ? 'Search notes' : 'Load notes first');
    };

    const applyPaginationVisibility = (stream, showStatus, showAction, keepRoot, focusStatus) => {
        stream.paginationStatus.hidden = !showStatus;
        stream.paginationAction.hidden = !showAction;
        stream.pagination.hidden = !showStatus && !showAction && !keepRoot;
        if (focusStatus && showStatus) {
            stream.paginationStatus.focus();
        }
    };

    const cancelPaginationTransition = (stream) => {
        cancelAnimations([stream.paginationMotionRun?.animation]);
        stream.paginationMotionRun = null;
    };

    const transitionPagination = (stream, outgoing, incoming, applyContent) => {
        cancelPaginationTransition(stream);
        if (
            !(outgoing instanceof HTMLElement)
            || !(incoming instanceof HTMLElement)
            || !canAnimateElement(outgoing, documentRef.defaultView)
            || !canAnimateElement(incoming, documentRef.defaultView)
        ) {
            applyContent();
            return;
        }

        stream.pagination.hidden = false;
        const duration = readCssTimeMs(stream.pagination, '--game-notes-pagination-swap-duration', 160);
        const run = {
            animation: playAnimation(outgoing, [
                { opacity: 1, transform: 'translateY(0)' },
                { opacity: 0, transform: 'translateY(-4px)' },
            ], {
                duration,
                easing: 'ease-in',
            }),
        };
        if (run.animation === null) {
            applyContent();
            return;
        }

        stream.paginationMotionRun = run;
        void settleAnimations([run.animation]).then(async () => {
            if (stream.paginationMotionRun !== run) {
                return;
            }

            applyContent();
            run.animation = playAnimation(incoming, [
                { opacity: 0, transform: 'translateY(4px)' },
                { opacity: 1, transform: 'translateY(0)' },
            ], {
                duration,
                easing: 'ease-out',
            });
            await settleAnimations([run.animation]);
            if (stream.paginationMotionRun === run) {
                stream.paginationMotionRun = null;
            }
        });
    };

    const transitionPaginationVisibility = (stream, showStatus, showAction, keepRoot, focusStatus) => {
        const outgoing = !stream.paginationStatus.hidden && !showStatus
            ? stream.paginationStatus
            : (!stream.paginationAction.hidden && !showAction ? stream.paginationAction : null);
        const incoming = showStatus && stream.paginationStatus.hidden
            ? stream.paginationStatus
            : (showAction && stream.paginationAction.hidden ? stream.paginationAction : null);
        transitionPagination(stream, outgoing, incoming, () => {
            applyPaginationVisibility(stream, showStatus, showAction, keepRoot, focusStatus);
        });
    };

    const setPaginationState = (stream, state, message = '') => {
        const previousState = stream.paginationPhase;
        stream.paginationPhase = state;
        const actionHadFocus = documentRef.activeElement === stream.paginationAction;
        stream.pagination.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
        if (state === 'loading') {
            stream.paginationAction.setAttribute('aria-disabled', 'true');
        } else {
            stream.paginationAction.removeAttribute('aria-disabled');
        }

        const showStatus = message !== '';
        const showAction = state === 'retry' || state === 'restart'
            || (state === 'ready' && autoPaginationUnavailable);
        const keepRoot = state === 'ready' && !showAction;
        const isFailureState = state === 'retry' || state === 'restart';
        const wasFailureState = previousState === 'retry' || previousState === 'restart';
        const swapFailureFeedback = (previousState === 'loading' && isFailureState)
            || (wasFailureState && state === 'loading');
        const applyContent = () => {
            stream.paginationStatus.textContent = message;
            if (isFailureState) {
                stream.paginationAction.textContent = 'Try again';
            } else if (state === 'ready') {
                stream.paginationAction.textContent = 'Load more notes';
            }
            if (isFailureState && message !== '') {
                stream.paginationAction.setAttribute(
                    'aria-label',
                    `${message} ${stream.paginationAction.textContent}`,
                );
            } else {
                stream.paginationAction.removeAttribute('aria-label');
            }

            if (swapFailureFeedback) {
                applyPaginationVisibility(
                    stream,
                    showStatus,
                    showAction,
                    keepRoot,
                    actionHadFocus && showStatus,
                );
                return;
            }

            transitionPaginationVisibility(
                stream,
                showStatus,
                showAction,
                keepRoot,
                actionHadFocus && showStatus,
            );
        };

        if (swapFailureFeedback) {
            transitionPagination(stream, stream.pagination, stream.pagination, applyContent);
        } else {
            applyContent();
        }
    };

    const observeNextPage = (stream) => {
        if (
            stream !== activeStream
            || stream.nextPageUrl === ''
            || stream.paginationPhase !== 'ready'
        ) {
            return;
        }

        if (autoPaginationUnavailable) {
            return;
        }

        try {
            observer ??= new globalThis.IntersectionObserver((entries) => {
                const current = activeStream;
                if (
                    current !== null
                    && current.paginationPhase === 'ready'
                    && entries.some((entry) => (
                        entry.isIntersecting
                        && entry.target === current.pagination
                    ))
                ) {
                    void loadPage(current, current.kind === 'browse');
                }
            }, {
                rootMargin: NOTES_PREFETCH_MARGIN,
            });
            observer?.unobserve(stream.pagination);
            observer.observe(stream.pagination);
        } catch (_error) {
            observer = null;
            autoPaginationUnavailable = true;
            setPaginationState(stream, 'ready');
        }
    };

    const renderInitialError = (stream) => {
        if (stream.currentChunk instanceof HTMLElement || stream.restartHadContent) {
            return;
        }

        const error = documentRef.createElement('p');
        error.className = 'game-note-empty';
        error.textContent = 'Notes could not be loaded.';
        stream.root.classList.remove('game-notes-stream-centered');
        stream.root.classList.add('game-notes-stream-empty-feedback');
        stream.results.replaceChildren(error);
        setSearchAvailability(stream.kind === 'search' ? 'ready' : 'error');
    };

    const renderInitialLoading = (stream) => {
        stream.root.classList.remove('game-notes-stream-empty-feedback');
        stream.root.classList.toggle('game-notes-stream-centered', stream.kind === 'search');
        if (stream.kind === 'search') {
            const loading = documentRef.createElement('p');
            loading.className = 'game-notes-loading';
            loading.setAttribute('role', 'status');
            loading.textContent = 'Searching notes...';
            stream.results.replaceChildren(loading);
        } else {
            const skeleton = documentRef.createElement('div');
            skeleton.className = 'game-notes-text-skeleton';
            skeleton.setAttribute('role', 'status');
            skeleton.setAttribute('aria-label', 'Loading notes');
            skeleton.replaceChildren(
                ...Array.from({ length: 4 }, () => documentRef.createElement('span')),
            );
            stream.results.replaceChildren(skeleton);
        }
        setSearchAvailability(stream.kind === 'search' ? 'ready' : 'loading');
    };

    const commitPage = (stream, page) => {
        const isContinuation = stream.currentChunk instanceof HTMLElement;
        if (!(stream.currentChunk instanceof HTMLElement)) {
            stream.results.replaceChildren(page.chunk);
            stream.currentChunk = page.chunk;
            stream.restartPending = false;
            stream.restartHadContent = false;
            stream.hasAttachmentWarning = page.warning instanceof HTMLElement;
            if (!countInitialized) {
                count.textContent = page.totalCount;
                countInitialized = true;
            }
            try {
                hydratePrivateAttachments?.(page.chunk);
            } catch (_error) {
                // Notes remain readable when optional signed-attachment hydration fails.
            }
        } else if (page.noteList instanceof HTMLElement) {
            if (page.warning instanceof HTMLElement && !stream.hasAttachmentWarning) {
                stream.currentChunk.append(page.warning);
                stream.hasAttachmentWarning = true;
            }
            page.noteList.classList.add('game-note-list-appended');
            stream.currentChunk.append(page.noteList);
            try {
                hydratePrivateAttachments?.(page.noteList);
            } catch (_error) {
                // Notes remain readable when optional signed-attachment hydration fails.
            }
        }

        stream.resultCount = Number.parseInt(stream.kind === 'search' ? page.matchedCount : page.totalCount, 10);
        stream.root.classList.remove('game-notes-stream-empty-feedback');
        stream.root.classList.toggle('game-notes-stream-centered', stream.resultCount === 0);
        stream.hasLoadedContinuation ||= isContinuation;
        stream.nextPageUrl = page.nextPageUrl;
        setSearchAvailability('ready');
    };

    const handleFailure = (stream, error) => {
        const hasContent = stream.currentChunk instanceof HTMLElement || stream.restartHadContent;
        renderInitialError(stream);
        if (error === NOTES_PAGE_REQUEST_ERROR.OFFLINE) {
            setPaginationState(stream, 'retry', 'You are offline. Reconnect and try again.');
        } else if (error === NOTES_PAGE_REQUEST_ERROR.CLIENT) {
            setPaginationState(stream, 'restart', 'The notes stream is no longer valid. Try loading it again from the beginning.');
        } else if (error === NOTES_PAGE_REQUEST_ERROR.RELOAD) {
            setPaginationState(stream, 'restart', 'Notes could not be continued safely. Try loading them again from the beginning.');
        } else {
            setPaginationState(stream, 'retry', hasContent ? 'Notes could not be loaded. Try again.' : 'Try again.');
        }
    };

    const ownsRequest = (stream, controller) => stream.requestController === controller
        && controller.signal.aborted === false;

    const startResultsFade = (stream) => {
        const view = documentRef.defaultView;
        if (
            typeof view?.matchMedia !== 'function'
            || view.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
            return null;
        }

        stream.results.classList.add('game-notes-results-filtering');
        return new Promise((resolve) => {
            if (typeof view.setTimeout === 'function') {
                view.setTimeout(resolve, SEARCH_RESULTS_SWAP_DELAY_MS);
            } else {
                globalThis.setTimeout(resolve, SEARCH_RESULTS_SWAP_DELAY_MS);
            }
        });
    };

    const startSearchPlaceholderFade = (stream) => (
        stream.kind === 'search' && !(stream.currentChunk instanceof HTMLElement)
            ? startResultsFade(stream)
            : null
    );

    const cancelNotesLanding = () => {
        landingStream = null;
        LANDING_INTENT_EVENTS.forEach((type) => {
            globalThis.removeEventListener?.(type, cancelNotesLanding, { capture: true });
        });
    };

    const armNotesLanding = (stream) => {
        if (globalThis.location?.hash !== '#notes') {
            return;
        }

        landingStream = stream;
        LANDING_INTENT_EVENTS.forEach((type) => {
            globalThis.addEventListener?.(type, cancelNotesLanding, { capture: true, passive: true });
        });
    };

    const consumeNotesLanding = (stream) => {
        if (stream !== landingStream) {
            return;
        }

        // The section's entrance reveal shifts it by a transform until it ends; aimed earlier, the scroll
        // would stop short by that shift. Input during the wait still cancels.
        void settleAnimations(notesSection.getAnimations?.() ?? []).then(() => {
            if (stream !== landingStream) {
                return;
            }

            cancelNotesLanding();
            if (globalThis.location?.hash === '#notes') {
                notesSection.scrollIntoView({ block: 'start', behavior: 'instant' });
            }
        });
    };

    async function loadPage(stream, allowAutoRetry) {
        if (stream !== activeStream || stream.requestController !== null || stream.nextPageUrl === '') {
            return;
        }

        const requestUrl = stream.nextPageUrl;
        const controller = new AbortController();
        stream.requestController = controller;
        stream.resumePending = false;
        stream.autoRetryAvailable = allowAutoRetry;
        observer?.unobserve(stream.pagination);
        const isInitial = !(stream.currentChunk instanceof HTMLElement);
        const hasVisibleContent = !isInitial || stream.restartHadContent;
        if (!hasVisibleContent) {
            const isReplacingFailure = stream.paginationPhase === 'retry'
                || stream.paginationPhase === 'restart';
            if (isReplacingFailure) {
                cancelPaginationTransition(stream);
                applyPaginationVisibility(stream, false, false, false, false);
            }
            renderInitialLoading(stream);
            setPaginationState(stream, 'loading');
            stream.results.setAttribute('aria-busy', 'true');
        } else {
            setPaginationState(stream, 'loading', isInitial ? 'Loading notes...' : 'Loading more notes...');
        }

        let succeeded = false;
        let failed = false;
        try {
            let outcome = await requestNotesPage({
                fetchImpl,
                url: requestUrl,
                signal: controller.signal,
                baseUrl,
                initialPageUrl,
                expectedQuery: stream.query,
            });
            if (!ownsRequest(stream, controller)) {
                return;
            }

            if (
                outcome.error === NOTES_PAGE_REQUEST_ERROR.RETRYABLE
                && stream.autoRetryAvailable
            ) {
                stream.autoRetryAvailable = false;
                setPaginationState(
                    stream,
                    'loading',
                    hasVisibleContent ? 'Loading notes failed. Retrying...' : '',
                );
                await new Promise((resolve) => globalThis.setTimeout(resolve, AUTO_RETRY_DELAY_MS));
                if (!ownsRequest(stream, controller)) {
                    return;
                }
                outcome = await requestNotesPage({
                    fetchImpl,
                    url: requestUrl,
                    signal: controller.signal,
                    baseUrl,
                    initialPageUrl,
                    expectedQuery: stream.query,
                });
                if (!ownsRequest(stream, controller)) {
                    return;
                }
            }

            if (outcome.page !== undefined) {
                const placeholderFade = startSearchPlaceholderFade(stream);
                if (placeholderFade !== null) {
                    await placeholderFade;
                    if (!ownsRequest(stream, controller)) {
                        return;
                    }
                }
                // The landing scroll follows this load; a growing height would clamp it.
                const shouldResizeSearchResults = isInitial && stream.kind === 'search' && stream !== landingStream;
                if (shouldResizeSearchResults) {
                    pendingStreamHeight = readStreamHeight(stream);
                }
                commitPage(stream, outcome.page);
                if (shouldResizeSearchResults) {
                    animateStreamHeight(stream);
                }
                stream.results.classList.remove('game-notes-results-filtering');
                succeeded = true;
            } else if (outcome.aborted !== true) {
                handleFailure(stream, outcome.error);
                failed = true;
            }
        } finally {
            if (!ownsRequest(stream, controller)) {
                return;
            }

            stream.requestController = null;
            stream.results.setAttribute('aria-busy', 'false');
            if (succeeded && stream.nextPageUrl === '') {
                setPaginationState(
                    stream,
                    'end',
                    stream.hasLoadedContinuation && stream.resultCount > 0 ? 'End of notes.' : '',
                );
            } else if (succeeded) {
                setPaginationState(stream, 'ready');
                observeNextPage(stream);
            }
            if (isInitial && (succeeded || failed)) {
                consumeNotesLanding(stream);
            }
        }
    }

    const pauseStream = (stream, resumePending = false) => {
        observer?.unobserve(stream.pagination);
        if (stream.requestController instanceof AbortController) {
            stream.requestController.abort();
            stream.requestController = null;
            stream.resumePending = resumePending;
        }
    };

    const restartStream = (stream) => {
        pauseStream(stream);
        stream.restartHadContent = stream.currentChunk instanceof HTMLElement;
        stream.currentChunk = null;
        stream.nextPageUrl = stream.kind === 'browse'
            ? initialUrl
            : buildNotesSearchPageUrl(initialPageUrl, stream.query);
        stream.resumePending = false;
        stream.autoRetryAvailable = false;
        stream.restartPending = true;
        stream.hasAttachmentWarning = false;
        stream.hasLoadedContinuation = false;
        if (stream.kind === 'browse' || browseStream.currentChunk === null) {
            countInitialized = false;
        }
        void loadPage(stream, stream.kind === 'browse');
    };

    const renderSearchLoadingPlaceholder = () => {
        cancelPaginationTransition(searchStream);
        renderInitialLoading(searchStream);
        searchStream.results.setAttribute('aria-busy', 'true');
        searchStream.pagination.hidden = true;
        searchStream.pagination.setAttribute('aria-busy', 'false');
        searchStream.paginationStatus.textContent = '';
        searchStream.paginationStatus.hidden = true;
        searchStream.paginationAction.hidden = true;
        searchStream.paginationAction.removeAttribute('aria-disabled');
        searchStream.paginationAction.textContent = 'Load more notes';
    };

    const resetSearchStream = (query) => {
        pauseStream(searchStream);
        searchStream.query = query;
        searchStream.currentChunk = null;
        searchStream.nextPageUrl = query === '' ? '' : buildNotesSearchPageUrl(initialPageUrl, query);
        searchStream.resumePending = false;
        searchStream.paginationPhase = 'ready';
        searchStream.restartPending = false;
        searchStream.restartHadContent = false;
        searchStream.hasAttachmentWarning = false;
        searchStream.hasLoadedContinuation = false;
        searchStream.resultCount = 0;
        renderSearchLoadingPlaceholder();
    };

    const readStreamHeight = (stream) => {
        const height = stream?.root?.getBoundingClientRect?.().height;

        return Number.isFinite(height) && height > 0 ? height : null;
    };

    const rememberActiveStreamHeight = () => {
        pendingStreamHeight ??= readStreamHeight(activeStream);
    };

    const cleanupStreamHeightRun = (run) => {
        run.root.classList.remove('game-notes-stream-resizing');
        run.root.style.removeProperty('height');
    };

    const animateStreamHeight = (stream) => {
        const previousHeight = pendingStreamHeight;
        pendingStreamHeight = null;
        if (previousHeight === null) {
            return;
        }

        const previousRun = streamHeightRun;
        streamHeightRun = null;
        if (previousRun !== null) {
            cancelAnimations([previousRun.animation]);
            cleanupStreamHeightRun(previousRun);
        }
        const nextHeight = readStreamHeight(stream);

        if (
            nextHeight === null
            || previousHeight === nextHeight
            || !canAnimateElement(stream.root, documentRef.defaultView)
        ) {
            return;
        }

        const duration = readCssTimeMs(stream.root, '--game-notes-stream-resize-duration', 300);
        stream.root.classList.add('game-notes-stream-resizing');
        stream.root.style.setProperty('height', `${nextHeight}px`);
        const animation = playAnimation(stream.root, [
            { height: `${previousHeight}px` },
            { height: `${nextHeight}px` },
        ], {
            duration,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        });
        if (animation === null) {
            stream.root.classList.remove('game-notes-stream-resizing');
            stream.root.style.removeProperty('height');
            return;
        }

        const run = { animation, root: stream.root };
        streamHeightRun = run;
        void settleAnimations([animation]).then(() => {
            if (streamHeightRun !== run) {
                return;
            }

            streamHeightRun = null;
            cleanupStreamHeightRun(run);
        });
    };

    const cancelStreamReveal = () => {
        cancelAnimations([streamRevealAnimation]);
        streamRevealAnimation = null;
    };

    const revealStreamResults = (stream) => {
        cancelStreamReveal();
        stream.results.classList.remove('game-notes-results-filtering');
        if (!canAnimateElement(stream.results, documentRef.defaultView)) {
            return;
        }

        const animation = playAnimation(stream.results, [
            { opacity: 0, transform: 'translateY(-6px)' },
            { opacity: 1, transform: 'translateY(0)' },
        ], {
            duration: SEARCH_RESULTS_SWAP_DELAY_MS,
            easing: 'ease-out',
        });
        if (animation === null) {
            return;
        }

        streamRevealAnimation = animation;
        void settleAnimations([animation]).then(() => {
            if (streamRevealAnimation === animation) {
                streamRevealAnimation = null;
            }
        });
    };

    const showStream = (stream, reveal = false) => {
        cancelStreamReveal();
        browseStream.root.hidden = stream !== browseStream;
        searchStream.root.hidden = stream !== searchStream;
        activeStream = stream;
        if (reveal) {
            revealStreamResults(stream);
        } else {
            stream.results.classList.remove('game-notes-results-filtering');
        }
    };

    const captureBrowseScroll = () => {
        if (typeof notesSection.getBoundingClientRect === 'function') {
            browseScrollOffset = -notesSection.getBoundingClientRect().top;
        }
    };

    const restoreBrowseScroll = () => {
        if (
            browseScrollOffset === null
            || typeof notesSection.getBoundingClientRect !== 'function'
            || typeof globalThis.scrollBy !== 'function'
        ) {
            browseScrollOffset = null;
            return;
        }

        globalThis.scrollBy({
            top: notesSection.getBoundingClientRect().top + browseScrollOffset,
            behavior: 'auto',
        });
        browseScrollOffset = null;
    };

    const syncBrowserQuery = (query) => {
        if (typeof globalThis.history?.replaceState !== 'function' || typeof globalThis.location?.href !== 'string') {
            return;
        }

        const url = new URL(globalThis.location.href);
        if (query === '') {
            url.searchParams.delete('notes_q');
        } else {
            url.searchParams.set('notes_q', query);
        }
        globalThis.history.replaceState(null, '', url.pathname + url.search + url.hash);
    };

    const activateBrowse = (reveal = false) => {
        resetSearchStream('');
        showStream(browseStream, reveal);
        animateStreamHeight(browseStream);
        restoreBrowseScroll();
        syncBrowserQuery('');

        if (
            browseStream.resumePending
            || (
                browseStream.currentChunk === null
                && browseStream.paginationPhase === 'ready'
            )
        ) {
            void loadPage(
                browseStream,
                browseStream.resumePending ? browseStream.autoRetryAvailable : true,
            );
        } else if (browseStream.paginationPhase === 'ready') {
            observeNextPage(browseStream);
        }
    };

    const activateSearch = (query) => {
        if (activeStream === browseStream) {
            captureBrowseScroll();
            pauseStream(browseStream, true);
        }

        const shouldReset = searchStream.query !== query;
        if (shouldReset) {
            resetSearchStream(query);
        }
        showStream(searchStream);
        animateStreamHeight(searchStream);
        syncBrowserQuery(query);

        if (
            searchStream.resumePending
            || (
                searchStream.currentChunk === null
                && searchStream.paginationPhase === 'ready'
            )
        ) {
            void loadPage(searchStream, false);
        } else if (searchStream.paginationPhase === 'ready') {
            observeNextPage(searchStream);
        }
    };

    const syncClearButton = () => {
        searchClear.hidden = searchInput.value === '';
    };

    const cancelQueuedSearch = () => {
        if (searchDebounceId === null) {
            return;
        }

        globalThis.clearTimeout(searchDebounceId);
        searchDebounceId = null;
    };

    const invalidateActiveSearch = (query) => {
        if (activeStream !== searchStream || searchStream.query === query) {
            return;
        }

        pauseStream(searchStream, true);
        activeStream = null;
    };

    const activateBrowseAfterFade = async (swapId) => {
        const fade = searchStream.root.hidden ? null : startResultsFade(searchStream);
        if (fade !== null) {
            await fade;
        }
        if (streamSwapId !== swapId || normalizeNotesQuery(searchInput.value) !== '') {
            return;
        }

        activateBrowse(true);
    };

    const runSearch = () => {
        notesSection.classList.remove(ARRIVED_FROM_LINK_CLASS);
        const swapId = ++streamSwapId;
        cancelStreamReveal();
        cancelQueuedSearch();
        const query = normalizeNotesQuery(searchInput.value);
        if (activeStream === searchStream && searchStream.query === query) {
            pendingStreamHeight = null;
            return;
        }
        rememberActiveStreamHeight();
        invalidateActiveSearch(query);
        if (query === '') {
            void activateBrowseAfterFade(swapId);
            return;
        }

        activateSearch(query);
    };

    const queueSearch = () => {
        cancelQueuedSearch();
        searchDebounceId = globalThis.setTimeout(() => {
            searchDebounceId = null;
            runSearch();
        }, SEARCH_DEBOUNCE_MS);
    };

    const handleSearchInput = () => {
        notesSection.classList.remove(ARRIVED_FROM_LINK_CLASS);
        streamSwapId += 1;
        cancelStreamReveal();
        syncClearButton();
        const query = normalizeNotesQuery(searchInput.value);
        rememberActiveStreamHeight();
        if (activeStream !== null) {
            const visibleQuery = activeStream === searchStream ? searchStream.query : '';
            activeStream.results.classList.toggle(
                'game-notes-results-filtering',
                visibleQuery !== query,
            );
        }
        invalidateActiveSearch(query);
        if (isComposing) {
            return;
        }

        query === '' ? runSearch() : queueSearch();
    };

    searchInput.addEventListener('compositionstart', () => {
        isComposing = true;
    });

    searchInput.addEventListener('compositionend', () => {
        isComposing = false;
        handleSearchInput();
    });

    searchInput.addEventListener('input', handleSearchInput);

    searchInput.addEventListener('search', () => {
        syncClearButton();
        runSearch();
    });

    searchClear.addEventListener('click', () => {
        searchInput.value = '';
        syncClearButton();
        runSearch();
        searchInput.focus();
    });

    searchForm.addEventListener('submit', (event) => {
        event.preventDefault();
        runSearch();
    });

    [browseStream, searchStream].forEach((stream) => {
        stream.paginationAction.addEventListener('click', () => {
            if (stream.paginationPhase === 'restart') {
                restartStream(stream);
            } else {
                void loadPage(stream, false);
            }
        });
    });

    syncClearButton();
    searchForm.hidden = false;
    setSearchAvailability('loading');
    const initialQuery = normalizeNotesQuery(searchInput.value);
    const arrivedFromLink = consumeArrivedFromLinkMarker();
    if (initialQuery === '') {
        showStream(browseStream);
        armNotesLanding(browseStream);
        void loadPage(browseStream, true);
    } else {
        if (arrivedFromLink) {
            notesSection.classList.add(ARRIVED_FROM_LINK_CLASS);
        }
        resetSearchStream(initialQuery);
        showStream(searchStream);
        armNotesLanding(searchStream);
        void loadPage(searchStream, false);
    }
}

/**
 * Reads the ranking link marker from the initial URL and drops it from the address bar, so a reload after
 * that arrival is a plain search. The query, the hash and the navigation context stay.
 */
function consumeArrivedFromLinkMarker() {
    if (typeof globalThis.location?.href !== 'string') {
        return false;
    }

    const url = new URL(globalThis.location.href);
    if (!url.searchParams.has(ARRIVED_FROM_LINK_PARAM)) {
        return false;
    }

    const arrived = url.searchParams.get(ARRIVED_FROM_LINK_PARAM) === ARRIVED_FROM_LINK_VALUE;
    url.searchParams.delete(ARRIVED_FROM_LINK_PARAM);
    if (typeof globalThis.history?.replaceState === 'function') {
        globalThis.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }

    return arrived;
}
